<template>
	<div>
		<dropdown v-if="roleOptions.length > 1" v-model="isActive" top>
			<btn icon>
				<avatar :model="selectedRole" :size="32"/>
			</btn>
			<template v-if="label" #title>
				<div class="font-bold text-sm p-8">{{ label }}</div>
			</template>
			<template #menu>
				<form-select only-options v-model="selectedIndex" :options="roleOptions" :name="props.name"/>
			</template>
		</dropdown>
		<avatar v-else :model="roles?.length ? roles[0] : null" :size="32" class="m-4"/>
	</div>
</template>

<script setup lang="ts">
import {useNuxtApp} from '#imports';
import {computed, ref, watch} from "vue";
import type {MainModelPreview} from "@spoferan/spoferan-ts-core";

const {$auth} = useNuxtApp();

const props = defineProps<{
	// The form data
	data: object,

	// The input name. Will be prepended by '_type' and '_id' for submit.
	name: string,

	// Whether the authenticated user can only select himself
	onlyAuth?: boolean,

	// The input label
	label?: string,

	// The display name of the initial creator to select
	selectedName?: string,

	// The selected role
	selected?: MainModelPreview
}>();

const emit = defineEmits(['update:selected']);

const isActive = ref(false);
const selectedIndex = ref(-1);

const roles = computed(() => {
	if (!$auth.user) {
		return [];
	}

	if (props.onlyAuth) {
		return [$auth.user];
	}

	return [
		$auth.user,
		...$auth.user.actAs
	];
});

const roleOptions = computed(() => {
	return roles.value ? roles.value.map((role: MainModelPreview, index) => {
		return {value: index, image: role.avatar_thumbnail, text: role.display_name}
	}) : []
});

const selectedRole = computed(() => {
	return selectedIndex.value > -1 ? roles.value[selectedIndex.value] : $auth.user;
});
watch(selectedRole, (newValue) => {
	emit('update:selected', newValue);
}, {immediate: true});

watch(roles, (newValue: MainModelPreview[]) => {
	if (props.selectedName) {
		const initialIndex = newValue.findIndex(role => role.display_name === props.selectedName);
		selectedIndex.value = initialIndex > -1 ? initialIndex : 0;
	}
}, {immediate: true});

watch(selectedIndex, (newValue) => {
	const role = roles.value[newValue];
	if (role) {
		props.data[`${props.name}_type`] = role.model_type;
		props.data[`${props.name}_id`] = role.id;
	}
	isActive.value = false;
});

</script>